/**
 * Camera module types. Use with CameraManager only.
 * For orbit-around-target cameras use Three.js OrbitControls instead.
 */

import type * as THREE from 'three';

/** Camera mode constants (orbital removed; use Three.js OrbitControls). */
export const CameraMode = {
    FIRST_PERSON: 'first-person',
    THIRD_PERSON: 'third-person',
    TOP_DOWN: 'top_down',
    FIXED: 'fixed',
    CUSTOM: 'custom'
} as const;

export type CameraMode = typeof CameraMode[keyof typeof CameraMode];

/** Input for cameras: mouse, touch, keys, joystick. */
export interface CameraInputProvider {
    getMouseDelta(): { x: number; y: number };
    getMousePosition(): { screen: { x: number; y: number }; canvas: { x: number; y: number }; normalized: { x: number; y: number } };
    getMouseWheelDelta(): { x: number; y: number };
    getPressedKeys(): string[];
    isMouseDragging(button?: number): boolean;

    // Optional touch support for mobile devices
    isTouchActive?(): boolean;
    getTouchCount?(): number;
    getTouchDelta?(): { x: number; y: number };
    getTouchPosition?(): { x: number; y: number };
    getPinchDelta?(): number;
    isPinching?(): boolean;

    // Optional virtual joystick support
    getJoystickVector?(id: string): { x: number; y: number };
    getJoystickAngle?(id: string): number;
    getJoystickDistance?(id: string): number;
    isJoystickActive?(id: string): boolean;
}

/** Target for camera (position; optional rotation/quaternion). */
export interface CameraTarget {
    position: THREE.Vector3;
    rotation?: THREE.Euler;
    quaternion?: THREE.Quaternion;
}

/** Base config: input, smoothing, pitch limits, collision. */
export interface BaseCameraConfig {
    /** Input provider for camera controls */
    inputProvider?: CameraInputProvider;
    /** Enable smooth camera movement */
    smoothing?: boolean;
    /** Smoothing factor (0-1, higher = more responsive) */
    smoothFactor?: number;
    /** Minimum pitch angle (radians) */
    minPitch?: number;
    /** Maximum pitch angle (radians) */
    maxPitch?: number;
    /** Enable collision detection */
    enableCollision?: boolean;
}

/** First-person: eyeHeight, sensitivity, head bob, sprint FOV. */
export interface FirstPersonCameraConfig extends BaseCameraConfig {
    /** Eye height offset from target position */
    eyeHeight?: number;
    /** Mouse sensitivity for looking around */
    mouseSensitivity?: number;
    /** Enable head bobbing effect */
    enableHeadBob?: boolean;
    /** Head bob intensity */
    headBobIntensity?: number;
    /** Field of view for sprinting effect */
    sprintFOVMultiplier?: number;
}

/** Third-person: distance, zoom, heightOffset, followTargetRotation, etc. */
export interface ThirdPersonCameraConfig extends BaseCameraConfig {
    /** Distance from target */
    distance?: number;
    /** Minimum distance (for zoom) */
    minDistance?: number;
    /** Maximum distance (for zoom) */
    maxDistance?: number;
    /** Height offset above target */
    heightOffset?: number;
    /** Mouse sensitivity for rotation */
    mouseSensitivity?: number;
    /** Enable camera rotation with target rotation */
    followTargetRotation?: boolean;
    /** Enable zoom with mouse wheel */
    enableZoom?: boolean;
    /** Zoom speed */
    zoomSpeed?: number;
    /** Initial yaw (horizontal rotation) */
    initialYaw?: number;
    /** Initial pitch (vertical angle) */
    initialPitch?: number;
}

/** Camera state snapshot (mode, position, rotation, etc.). */
export interface CameraState {
    mode: CameraMode;
    position: THREE.Vector3;
    rotation: THREE.Euler;
    fov?: number;
    target?: THREE.Vector3;
}

/** Camera controller contract (used by CameraManager). */
export interface ICameraController {
    /** Current camera mode */
    readonly mode: CameraMode;

    /** Update camera (called every frame) */
    update(deltaTime: number): void;

    /** Get the camera instance */
    getCamera(): THREE.Camera;

    /** Set camera target */
    setTarget(target: CameraTarget): void;

    /** Get current target */
    getTarget(): CameraTarget | null;

    /** Reset camera to initial state */
    reset(): void;

    /** Get camera state */
    getState(): CameraState;

    /** Dispose resources */
    dispose(): void;
}

